/**
 * Bus<EventMap>
 *
 * Typed pub/sub with three usage modes:
 *   1. Channel-specific:  bus.on('tiw.flow.tick', (ev) => ...)
 *   2. Wildcard prefix:   bus.on('tiw.flow.*',    (ev) => ...) — payload typed to union of matches
 *   3. All events:        bus.on('*',            (ev) => ...) — payload typed to full union
 *
 * Listeners receive the full event object (typed by the channel key in EventMap).
 * `on` returns an unsubscribe handle so callers can dispose without keeping
 * a reference to the listener function.
 */

/** Constraint on the event map: keys are channel names, values include `channel`. */
export type AnyEventMap = Record<string, { readonly channel: string }>;

export type BusListener<E extends AnyEventMap, K extends keyof E> = (event: E[K]) => void;

export type BusWildcardListener<E extends AnyEventMap> = (event: E[keyof E]) => void;

/** Channel pattern: exact key, "prefix.*" wildcard, or "*" for all. */
export type ChannelPattern<E extends AnyEventMap> = keyof E | `${string}.*` | '*';

export interface BusSubscription {
  unsubscribe(): void;
}

interface WildcardEntry<E extends AnyEventMap> {
  readonly pattern: string;
  readonly prefix: string;
  readonly listener: BusWildcardListener<E>;
}

export class Bus<E extends AnyEventMap> {
  private readonly exact = new Map<keyof E, Set<BusListener<E, keyof E>>>();
  private readonly wildcards: Array<WildcardEntry<E>> = [];

  on<K extends keyof E>(channel: K, listener: BusListener<E, K>): BusSubscription;
  on(pattern: `${string}.*` | '*', listener: BusWildcardListener<E>): BusSubscription;
  on(
    pattern: ChannelPattern<E>,
    listener: BusListener<E, keyof E> | BusWildcardListener<E>,
  ): BusSubscription {
    if (typeof pattern === 'string' && (pattern === '*' || pattern.endsWith('.*'))) {
      const entry: WildcardEntry<E> = {
        pattern,
        prefix: pattern === '*' ? '' : pattern.slice(0, -2),
        listener: listener as BusWildcardListener<E>,
      };
      this.wildcards.push(entry);
      return {
        unsubscribe: () => {
          const idx = this.wildcards.indexOf(entry);
          if (idx >= 0) this.wildcards.splice(idx, 1);
        },
      };
    }
    const key = pattern as keyof E;
    let set = this.exact.get(key);
    if (!set) {
      set = new Set();
      this.exact.set(key, set);
    }
    const fn = listener as BusListener<E, keyof E>;
    set.add(fn);
    return {
      unsubscribe: () => {
        set?.delete(fn);
        if (set && set.size === 0) this.exact.delete(key);
      },
    };
  }

  off<K extends keyof E>(channel: K, listener: BusListener<E, K>): void {
    const set = this.exact.get(channel);
    set?.delete(listener as BusListener<E, keyof E>);
    if (set && set.size === 0) this.exact.delete(channel);
  }

  once<K extends keyof E>(channel: K, listener: BusListener<E, K>): BusSubscription {
    const sub = this.on(channel, (event) => {
      sub.unsubscribe();
      listener(event);
    });
    return sub;
  }

  emit<K extends keyof E>(channel: K, event: E[K]): void {
    const exact = this.exact.get(channel);
    if (exact) {
      // Snapshot to tolerate listeners that unsubscribe during dispatch.
      for (const fn of [...exact]) fn(event);
    }
    if (this.wildcards.length > 0) {
      const ch = String(channel);
      for (const w of [...this.wildcards]) {
        if (w.prefix === '' || ch.startsWith(w.prefix)) {
          w.listener(event as E[keyof E]);
        }
      }
    }
  }

  /** Remove every listener. Useful for hot-reload and test teardown. */
  clear(): void {
    this.exact.clear();
    this.wildcards.length = 0;
  }

  listenerCount(channel?: keyof E): number {
    if (channel === undefined) {
      let total = this.wildcards.length;
      for (const s of this.exact.values()) total += s.size;
      return total;
    }
    return this.exact.get(channel)?.size ?? 0;
  }
}
